import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL ?? 'https://echoes-of-the-word-production.up.railway.app/api/v1';
const api = axios.create({ baseURL: API });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const T = {
  burgundy: '#6B1E2E', parchment: '#F5EDD8', parchmentLight: '#FBF6EC',
  ink: '#2C2015', inkLight: '#8C7A65', border: '#D4C5B0',
  success: '#2D6A4F', gold: '#B8860B', white: '#FFFFFF', error: '#B91C1C',
};

const inp: React.CSSProperties = {
  width: '100%', padding: '9px 12px', border: `0.5px solid ${T.border}`,
  borderRadius: 8, fontSize: 13, boxSizing: 'border-box', fontFamily: 'system-ui',
};
const btn = (color = T.burgundy): React.CSSProperties => ({
  background: color, color: T.white, border: 'none', borderRadius: 8,
  padding: '9px 18px', cursor: 'pointer', fontSize: 13, fontFamily: 'system-ui', fontWeight: 600,
});
const btnSm = (color = T.burgundy): React.CSSProperties => ({
  ...btn(color), padding: '4px 12px', fontSize: 12,
});

function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div style={{ background: T.white, borderRadius: 12, padding: '20px 24px', border: `0.5px solid ${T.border}`, flex: 1, minWidth: 180 }}>
      <div style={{ fontSize: 12, color: T.inkLight, marginBottom: 6, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: 32, fontWeight: 700, color: color ?? T.ink, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: T.inkLight, marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: T.white, borderRadius: 16, padding: 32, width: 480, maxWidth: '90vw', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 20, color: T.ink, margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: T.inkLight }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.inkLight, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

export function SuperDashboard({ onLogout }: { onLogout?: () => void }) {
  const [data,       setData]       = useState<any>(null);
  const [loading,    setLoading]    = useState(true);
  const [loadError,  setLoadError]  = useState('');
  const [churches,   setChurches]   = useState<any[]>([]);
  const [tab,        setTab]        = useState<'overview'|'churches'|'users'|'finance'|'promo'|'config'|'notifications'|'music'|'dbquery'>('overview');
  const [users,      setUsers]      = useState<any>(null);
  const [finance,    setFinance]    = useState<any>(null);
  const [promo,      setPromo]      = useState<any>(null);
  const [promoCount, setPromoCount] = useState(5);
  const [promoDesc,  setPromoDesc]  = useState('');
  const [showAddUser,    setShowAddUser]    = useState(false);
  const [showAddChurch,  setShowAddChurch]  = useState(false);
  const [editingUser,    setEditingUser]    = useState<any>(null);
  const [editingChurch,  setEditingChurch]  = useState<any>(null);
  const [notifications,  setNotifications]  = useState<any[]>([]);
  const [purgeDays,      setPurgeDays]      = useState(30);
  // Music catalogue (global content — AI-original albums)
  const [albums,         setAlbums]         = useState<any[]>([]);
  const [editingAlbum,   setEditingAlbum]   = useState<any>(null);
  const [showAddAlbum,   setShowAddAlbum]   = useState(false);
  // Compose-notification state
  const [composeTitle,   setComposeTitle]   = useState('');
  const [composeBody,    setComposeBody]    = useState('');
  const [composeAll,     setComposeAll]     = useState(true);
  const [composeChurchIds, setComposeChurchIds] = useState<number[]>([]);
  const [sending,        setSending]        = useState(false);
  // SQL query runner state
  const [sqlText,        setSqlText]        = useState('SELECT id, name FROM churches LIMIT 10;');
  const [sqlAllowWrites, setSqlAllowWrites] = useState(false);
  const [sqlResult,      setSqlResult]      = useState<any>(null);
  const [sqlRunning,     setSqlRunning]     = useState(false);

  // church lookup map: id → name
  const churchMap: Record<number, string> = {};
  churches.forEach((c: any) => { churchMap[c.id] = c.name; });

  const loadChurches = () => api.get('/admin/churches').then(r => setChurches(r.data.data));
  const loadUsers    = () => api.get('/admin/users?limit=100').then(r => setUsers(r.data.data));
  const loadFinance  = () => api.get(`/admin/finance/report?from=2024-01-01&to=${new Date().toISOString().split('T')[0]}`).then(r => setFinance(r.data.data));
  const loadPromo    = () => api.get('/admin/promo-codes').then(r => setPromo(r.data.data));
  const loadMusic    = () => api.get('/admin/music/albums')
    .then(r => setAlbums(r.data.data ?? r.data ?? []))
    .catch(() => setAlbums([]));

  useEffect(() => {
    api.get('/admin/dashboard')
      .then(r => { setData(r.data.data); setLoading(false); loadChurches(); })
      .catch(e => {
        const status = e?.response?.status;
        const msg    = e?.response?.data?.message ?? e?.message ?? 'Unknown error';
        setLoadError(status === 403 ? 'Access denied — your account does not have super_admin privileges.' :
                     status === 401 ? 'Session expired — please sign out and sign back in.' :
                     `Failed to load dashboard (${status ?? 'network error'}): ${msg}`);
        setLoading(false);
      });
  }, []);

  const generatePromos = async () => {
    await api.post('/payments/promo-codes/generate', { count: promoCount, description: promoDesc });
    loadPromo();
  };

  const toggleFlag = async (churchId: number, flag: string, enabled: boolean) => {
    await api.patch(`/admin/churches/${churchId}/features`, { flag, enabled });
    loadChurches();
  };

  const updateSplit = async (churchId: number, pct: number) => {
    await api.patch(`/admin/churches/${churchId}/split`, { splitPct: pct });
    loadChurches();
  };

  const deleteUser = async (userId: number, name: string) => {
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    await api.delete(`/admin/users/${userId}`);
    loadUsers();
    api.get('/admin/dashboard').then(r => setData(r.data.data));
  };

  const refreshUsers = () => {
    loadUsers();
    api.get('/admin/dashboard').then(r => setData(r.data.data));
  };

  const deactivateChurch = async (churchId: number, name: string) => {
    if (!window.confirm(`Deactivate "${name}"? Users linked to this church will still exist.`)) return;
    await api.delete(`/admin/churches/${churchId}`);
    loadChurches();
    api.get('/admin/dashboard').then(r => setData(r.data.data));
  };

  const reactivateChurch = async (churchId: number, name: string) => {
    if (!window.confirm(`Reactivate "${name}"?`)) return;
    await api.patch(`/admin/churches/${churchId}`, { isActive: true });
    loadChurches();
    api.get('/admin/dashboard').then(r => setData(r.data.data));
  };

  const deleteChurchHard = async (churchId: number, name: string) => {
    if (!window.confirm(`DELETE "${name}" permanently? This cannot be undone. Users will be detached.`)) return;
    if (!window.confirm(`Really delete "${name}"? Type the church name in the next prompt to confirm.`)) return;
    const typed = window.prompt(`Type the church name exactly to confirm deletion:`);
    if (typed !== name) { alert('Name did not match — cancelled.'); return; }
    await api.delete(`/admin/churches/${churchId}/hard`);
    loadChurches();
    api.get('/admin/dashboard').then(r => setData(r.data.data));
  };

  const loadNotifications = () => {
    api.get('/notifications/all?limit=200').then(r => setNotifications(r.data.data ?? []))
      .catch(() => setNotifications([]));
  };

  const deleteNotification = async (id: number) => {
    if (!window.confirm('Delete this notification? Users will no longer see it.')) return;
    await api.delete(`/notifications/${id}`);
    loadNotifications();
  };

  const purgeOldNotifications = async () => {
    if (!window.confirm(`Delete all notifications older than ${purgeDays} days?`)) return;
    const { data } = await api.delete(`/notifications/purge/older-than?days=${purgeDays}`);
    alert(`Deleted ${data.data?.deleted ?? 0} notification(s).`);
    loadNotifications();
  };

  const publishAlbum = async (id: number, name: string, publish: boolean) => {
    if (publish && !window.confirm(
      `Publish "${name}"?\n\nConfirm you have listened to every track for accidental similarity to existing songs. Members of enabled parishes will be able to hear it.`
    )) return;
    try {
      await api.post(`/admin/music/albums/${id}/publish`, { publish });
      loadMusic();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Could not change publish state.');
    }
  };

  const deleteAlbum = async (id: number, name: string) => {
    if (!window.confirm(`Delete "${name}" and all its tracks? This cannot be undone.`)) return;
    await api.delete(`/admin/music/albums/${id}`);
    loadMusic();
  };

  const toggleComposeChurch = (id: number) => {
    setComposeChurchIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  };

  const sendNotification = async () => {
    if (!composeTitle.trim() || !composeBody.trim()) {
      alert('Please enter both a title and a message.');
      return;
    }
    const target: number[] | 'all' = composeAll ? 'all' : composeChurchIds;
    if (!composeAll && composeChurchIds.length === 0) {
      alert('Select at least one church, or choose "All churches".');
      return;
    }
    const label = composeAll ? 'ALL churches' : `${composeChurchIds.length} church(es)`;
    if (!window.confirm(`Send "${composeTitle}" to ${label}?`)) return;
    setSending(true);
    try {
      const { data } = await api.post('/notifications/send-bulk', {
        churchIds: target, title: composeTitle.trim(), body: composeBody.trim(), type: 'general',
      });
      alert(`Sent to ${data.data?.churches ?? 0} church(es).`);
      setComposeTitle(''); setComposeBody(''); setComposeChurchIds([]); setComposeAll(true);
      loadNotifications();
    } catch (e: any) {
      alert(`Failed to send: ${e?.response?.data?.message ?? e?.message ?? 'Unknown error'}`);
    } finally {
      setSending(false);
    }
  };

  const runSql = async () => {
    if (!sqlText.trim()) return;
    setSqlRunning(true);
    setSqlResult(null);
    try {
      const { data } = await api.post(
        `/admin/query${sqlAllowWrites ? '?allowWrites=true' : ''}`,
        { sql: sqlText },
      );
      setSqlResult(data.data ?? data);
    } catch (e: any) {
      setSqlResult({ error: e.response?.data?.message ?? e.message });
    } finally {
      setSqlRunning(false);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'Georgia', fontSize: 18, color: T.burgundy }}>
      Loading dashboard…
    </div>
  );

  if (loadError) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', fontFamily: 'system-ui', gap: 16, padding: 24 }}>
      <div style={{ fontSize: 32 }}>⚠️</div>
      <div style={{ fontSize: 15, color: T.error, textAlign: 'center', maxWidth: 480 }}>{loadError}</div>
      <button onClick={() => { setLoading(true); setLoadError('');
        api.get('/admin/dashboard').then(r => { setData(r.data.data); setLoading(false); loadChurches(); })
          .catch(e => { const status = e?.response?.status; const msg = e?.response?.data?.message ?? e?.message ?? 'Unknown error';
            setLoadError(status === 403 ? 'Access denied — your account does not have super_admin privileges.' : status === 401 ? 'Session expired — please sign out and sign back in.' : `Failed to load dashboard (${status ?? 'network error'}): ${msg}`);
            setLoading(false); }); }}
        style={{ background: T.burgundy, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 24px', fontSize: 14, cursor: 'pointer' }}>
        Retry
      </button>
      <button onClick={onLogout} style={{ background: 'none', border: 'none', color: T.inkLight, fontSize: 13, cursor: 'pointer' }}>Sign out</button>
    </div>
  );

  const TABS: [string, string][] = [
    ['overview','Overview'],['churches','Churches'],['users','Users'],
    ['notifications','Notifications'],
    ['music','Music'],
    ['finance','Finance'],['promo','Promo Codes'],
    ['config','Settings'],['dbquery','DB Query'],
  ];
  const FLAGS = ['gallery','events','livestream','forums','messaging','bulk_email','study_groups','music_streaming'];

  return (
    <div style={{ fontFamily: 'system-ui', background: T.parchmentLight, minHeight: '100vh' }}>

      {/* Sidebar */}
      <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: 220, background: T.burgundy, padding: '24px 0', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '0 20px 24px', borderBottom: `0.5px solid rgba(255,255,255,0.15)` }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: 16, color: T.white, fontWeight: 600 }}>✝ Echoes of the Word</div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>Super Admin</div>
        </div>
        <nav style={{ flex: 1, padding: '12px 0' }}>
          {TABS.map(([id, label]) => (
            <button key={id} onClick={() => {
              setTab(id as any);
              if (id === 'users') loadUsers();
              if (id === 'finance') loadFinance();
              if (id === 'promo') loadPromo();
              if (id === 'music') loadMusic();
            }} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 20px', background: tab === id ? 'rgba(255,255,255,0.15)' : 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: tab === id ? T.white : 'rgba(255,255,255,0.65)', fontFamily: 'system-ui', fontWeight: tab === id ? 600 : 400, borderLeft: `3px solid ${tab === id ? T.white : 'transparent'}` }}>
              {label}
            </button>
          ))}
        </nav>
        <div style={{ padding: '16px 20px', borderTop: `0.5px solid rgba(255,255,255,0.15)`, fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
          <button onClick={onLogout} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 12, cursor: 'pointer', fontFamily: 'system-ui', padding: 0, marginBottom: 8, display: 'block' }}>Sign Out</button>
          v1.0.0
        </div>
      </div>

      {/* Main */}
      <div style={{ marginLeft: 220, padding: 32 }}>

        {/* ── Overview ── */}
        {tab === 'overview' && data && (
          <div>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 26, color: T.ink, marginBottom: 24 }}>Dashboard</h1>
            <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
              <StatCard label="Total Users"      value={data.stats.totalUsers.toString()}                              sub="Active accounts" />
              <StatCard label="Churches"         value={data.stats.totalChurches.toString()}                           sub="Active parishes" />
              <StatCard label="Total Donations"  value={`R${parseFloat(data.stats.totalRevenue  || 0).toFixed(2)}`}    sub="All time" color={T.success} />
              <StatCard label="Platform Revenue" value={`R${parseFloat(data.stats.platformRevenue || 0).toFixed(2)}`}  sub="Your share" color={T.gold} />
            </div>

            <div style={{ background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}`, overflow: 'hidden', marginBottom: 24 }}>
              <div style={{ padding: '16px 20px', borderBottom: `0.5px solid ${T.border}`, fontFamily: 'Georgia, serif', fontSize: 16, fontWeight: 600, color: T.ink }}>Recent Donations</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: T.parchmentLight }}>
                  {['User','Church','Amount','Church Share','Platform Fee','Status','Date'].map(h => (
                    <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.inkLight, letterSpacing: '0.5px' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(data.recentPayments ?? []).map((p: any) => (
                    <tr key={p.id} style={{ borderBottom: `0.5px solid ${T.border}` }}>
                      {[`#${p.userId}`, churchMap[p.churchId] ?? `#${p.churchId}`, `R${p.amount}`, `R${p.churchShare}`, `R${p.platformFee}`, p.status, p.paidAt ? new Date(p.paidAt).toLocaleDateString() : '—'].map((c, i) => (
                        <td key={i} style={{ padding: '10px 14px', fontSize: 13, color: T.ink }}>{c}</td>
                      ))}
                    </tr>
                  ))}
                  {!(data.recentPayments?.length) && <tr><td colSpan={7} style={{ padding: '16px 14px', fontSize: 13, color: T.inkLight, textAlign: 'center' }}>No donations yet</td></tr>}
                </tbody>
              </table>
            </div>

            <div style={{ background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}`, overflow: 'hidden' }}>
              <div style={{ padding: '16px 20px', borderBottom: `0.5px solid ${T.border}`, fontFamily: 'Georgia, serif', fontSize: 16, fontWeight: 600, color: T.ink }}>Recent Registrations</div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: T.parchmentLight }}>
                  {['Name','Email','Role','Parish','Joined'].map(h => (
                    <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.inkLight }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(data.recentUsers ?? []).map((u: any) => (
                    <tr key={u.id} style={{ borderBottom: `0.5px solid ${T.border}` }}>
                      {[`${u.firstName} ${u.lastName}`, u.email, u.role, churchMap[u.churchId] ?? `#${u.churchId}`, new Date(u.createdAt).toLocaleDateString()].map((c, i) => (
                        <td key={i} style={{ padding: '10px 14px', fontSize: 13, color: T.ink }}>{c}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Churches ── */}
        {tab === 'churches' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 26, color: T.ink, margin: 0 }}>Churches</h1>
              <button style={btn()} onClick={() => setShowAddChurch(true)}>+ Add Church</button>
            </div>
            {churches.map((c: any) => (
              <div key={c.id} style={{ background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}`, marginBottom: 16, overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', borderBottom: `0.5px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontFamily: 'Georgia, serif', fontSize: 17, fontWeight: 600, color: T.ink, display: 'flex', alignItems: 'center', gap: 8 }}>
                      {c.name}
                      {c.isActive === false && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: T.error, background: `${T.error}15`, padding: '2px 8px', borderRadius: 99, letterSpacing: '0.5px' }}>INACTIVE</span>
                      )}
                    </div>
                    <div style={{ fontSize: 12, color: T.inkLight, marginTop: 2 }}>{c.city}, {c.country} · {c.memberCount ?? 0} members · R{parseFloat(c.churchRevenue ?? 0).toFixed(2)} donated</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <label style={{ fontSize: 12, color: T.inkLight }}>Split %</label>
                    <input type="number" min={0} max={100} defaultValue={c.donationSplitPct} onBlur={e => updateSplit(c.id, parseInt(e.target.value))}
                      style={{ width: 60, padding: '4px 8px', border: `0.5px solid ${T.border}`, borderRadius: 6, fontSize: 13 }} />
                    <button style={btnSm(T.gold)}    onClick={() => setEditingChurch(c)}>Edit</button>
                    {c.isActive === false ? (
                      <button style={btnSm(T.success)} onClick={() => reactivateChurch(c.id, c.name)}>Reactivate</button>
                    ) : (
                      <button style={btnSm('#B91C1C')} onClick={() => deactivateChurch(c.id, c.name)}>Deactivate</button>
                    )}
                    <button style={btnSm('#7F1D1D')} onClick={() => deleteChurchHard(c.id, c.name)}>Delete</button>
                  </div>
                </div>
                <div style={{ padding: '12px 20px' }}>
                  <div style={{ fontSize: 11, color: T.inkLight, fontWeight: 600, letterSpacing: '0.5px', marginBottom: 10 }}>FEATURE FLAGS</div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {FLAGS.map(flag => {
                      const enabled = c.featureFlags?.[flag] === true;
                      return (
                        <button key={flag} onClick={() => toggleFlag(c.id, flag, !enabled)}
                          style={{ padding: '5px 12px', borderRadius: 99, fontSize: 12, border: `0.5px solid ${enabled ? T.success : T.border}`, background: enabled ? `${T.success}18` : T.parchmentLight, color: enabled ? T.success : T.inkLight, cursor: 'pointer', fontFamily: 'system-ui', fontWeight: 500 }}>
                          {enabled ? '✓' : '○'} {flag.replace('_', ' ')}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Notifications ── */}
        {tab === 'notifications' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 26, color: T.ink, margin: 0 }}>Notifications</h1>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <label style={{ fontSize: 12, color: T.inkLight }}>Delete older than</label>
                <input type="number" min={1} value={purgeDays} onChange={e => setPurgeDays(parseInt(e.target.value) || 30)}
                       style={{ width: 60, padding: '4px 8px', border: `0.5px solid ${T.border}`, borderRadius: 6, fontSize: 13 }} />
                <span style={{ fontSize: 12, color: T.inkLight }}>days</span>
                <button style={btnSm('#B91C1C')} onClick={purgeOldNotifications}>Purge</button>
                <button style={btnSm()} onClick={loadNotifications}>Refresh</button>
              </div>
            </div>

            {/* ── Compose & send ── */}
            <div style={{ background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}`, padding: 20, marginBottom: 24 }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: 16, fontWeight: 600, color: T.ink, marginBottom: 14 }}>Send a Notification</div>
              <input
                placeholder="Title"
                value={composeTitle}
                maxLength={100}
                onChange={e => setComposeTitle(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: `0.5px solid ${T.border}`, borderRadius: 8, fontSize: 14, marginBottom: 10, boxSizing: 'border-box' }}
              />
              <textarea
                placeholder="Message"
                value={composeBody}
                maxLength={500}
                onChange={e => setComposeBody(e.target.value)}
                rows={3}
                style={{ width: '100%', padding: '10px 12px', border: `0.5px solid ${T.border}`, borderRadius: 8, fontSize: 14, marginBottom: 14, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }}
              />
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: T.ink, cursor: 'pointer', fontWeight: 600 }}>
                  <input type="checkbox" checked={composeAll} onChange={e => setComposeAll(e.target.checked)} />
                  All active churches
                </label>
              </div>
              {!composeAll && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, maxHeight: 160, overflowY: 'auto' }}>
                  {churches.filter((c: any) => c.isActive !== false).length === 0 &&
                    <span style={{ fontSize: 13, color: T.inkLight }}>No active churches.</span>}
                  {churches.filter((c: any) => c.isActive !== false).map((c: any) => {
                    const sel = composeChurchIds.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        onClick={() => toggleComposeChurch(c.id)}
                        style={{
                          padding: '6px 12px', borderRadius: 99, fontSize: 13, cursor: 'pointer',
                          border: `1px solid ${sel ? T.burgundy ?? '#6B1E2E' : T.border}`,
                          background: sel ? (T.burgundy ?? '#6B1E2E') : T.white,
                          color: sel ? T.white : T.ink,
                        }}
                      >
                        {c.name}
                      </button>
                    );
                  })}
                </div>
              )}
              <button
                onClick={sendNotification}
                disabled={sending}
                style={{ ...btnSm(T.burgundy ?? '#6B1E2E'), padding: '10px 20px', fontSize: 14, opacity: sending ? 0.6 : 1 }}
              >
                {sending ? 'Sending…' : 'Send Notification'}
              </button>
            </div>

            {notifications.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: T.inkLight, background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}` }}>
                No notifications loaded. Click Refresh to load.
              </div>
            ) : (
              <div style={{ background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}`, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: T.parchmentLight }}>
                    {['Title','Body','Type','Sent','Church',''].map(h => (
                      <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.inkLight }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {notifications.map((n: any) => (
                      <tr key={n.id} style={{ borderBottom: `0.5px solid ${T.border}` }}>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: T.ink, fontWeight: 600 }}>{n.title}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: T.ink, maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.body}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: T.inkLight }}>{n.type}</td>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: T.inkLight }}>{new Date(n.sentAt ?? n.createdAt).toLocaleString()}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: T.inkLight }}>{churchMap[n.churchId] ?? `#${n.churchId}`}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <button style={btnSm('#B91C1C')} onClick={() => deleteNotification(n.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Music ── */}
        {tab === 'music' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 26, color: T.ink, margin: 0 }}>Music</h1>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={btnSm()} onClick={loadMusic}>Refresh</button>
                <button style={btn()} onClick={() => setShowAddAlbum(true)}>+ New Album</button>
              </div>
            </div>
            <div style={{ fontSize: 12, color: T.inkLight, marginBottom: 20, lineHeight: 1.6 }}>
              Global catalogue — the same for every parish. Members only see it if their church has the
              <strong> music_streaming</strong> flag enabled (Churches tab). Albums stay hidden until published.
            </div>

            {albums.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: T.inkLight, background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}` }}>
                No albums yet. Click “+ New Album” to create one.
              </div>
            ) : albums.map((a: any) => (
              <div key={a.id} style={{ background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}`, marginBottom: 12, padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontFamily: 'Georgia, serif', fontSize: 17, fontWeight: 600, color: T.ink, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {a.title}
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.5px', padding: '2px 8px', borderRadius: 99,
                      color: a.isPublished ? T.success : T.inkLight,
                      background: a.isPublished ? `${T.success}15` : `${T.inkLight}15`,
                    }}>{a.isPublished ? 'PUBLISHED' : 'DRAFT'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: T.inkLight, marginTop: 3 }}>
                    {a.artist || 'Unknown artist'} · {a.genre} · {a.trackCount ?? 0} track{(a.trackCount ?? 0) === 1 ? '' : 's'}
                    {a.aiTool ? ` · ${a.aiTool}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={btnSm(T.gold)} onClick={() => setEditingAlbum(a)}>Manage</button>
                  {a.isPublished
                    ? <button style={btnSm(T.inkLight)} onClick={() => publishAlbum(a.id, a.title, false)}>Unpublish</button>
                    : <button style={btnSm(T.success)} onClick={() => publishAlbum(a.id, a.title, true)}>Publish</button>}
                  <button style={btnSm('#B91C1C')} onClick={() => deleteAlbum(a.id, a.title)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── DB Query ── */}
        {tab === 'dbquery' && (
          <div>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 26, color: T.ink, margin: 0, marginBottom: 8 }}>Database Query</h1>
            <p style={{ fontSize: 13, color: T.inkLight, margin: '0 0 16px' }}>
              Run read-only SQL by default. Tick "Allow writes" to enable INSERT/UPDATE/DELETE.
              DROP, TRUNCATE, GRANT and multi-statement queries are always rejected.
            </p>

            <textarea
              value={sqlText}
              onChange={e => setSqlText(e.target.value)}
              spellCheck={false}
              style={{
                width: '100%', minHeight: 160, padding: 14,
                fontFamily: 'Menlo, Monaco, monospace', fontSize: 13,
                border: `0.5px solid ${T.border}`, borderRadius: 10,
                background: T.white, color: T.ink, boxSizing: 'border-box',
              }}
              placeholder="SELECT id, name FROM churches LIMIT 10;"
            />

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
              <label style={{ fontSize: 13, color: T.ink, display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="checkbox" checked={sqlAllowWrites} onChange={e => setSqlAllowWrites(e.target.checked)} />
                Allow writes (UPDATE / INSERT / DELETE)
              </label>
              <button style={btn()} disabled={sqlRunning} onClick={runSql}>
                {sqlRunning ? 'Running…' : 'Run query'}
              </button>
            </div>

            {sqlResult?.error && (
              <div style={{ marginTop: 16, padding: 14, background: '#FEE2E2', borderRadius: 8, color: T.error, fontFamily: 'Menlo, monospace', fontSize: 13 }}>
                {sqlResult.error}
              </div>
            )}

            {sqlResult && !sqlResult.error && (
              <div style={{ marginTop: 16, background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}`, overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', background: T.parchmentLight, fontSize: 12, color: T.inkLight, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{sqlResult.rowCount} row(s){sqlResult.truncated ? ' · showing first 500' : ''}</span>
                  <span>{sqlResult.durationMs} ms</span>
                </div>
                <div style={{ overflow: 'auto', maxHeight: 500 }}>
                  {Array.isArray(sqlResult.rows) && sqlResult.rows.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead><tr style={{ background: T.parchmentLight, position: 'sticky', top: 0 }}>
                        {Object.keys(sqlResult.rows[0]).map(c => (
                          <th key={c} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: T.inkLight, borderBottom: `0.5px solid ${T.border}` }}>{c}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {sqlResult.rows.map((row: any, i: number) => (
                          <tr key={i} style={{ borderBottom: `0.5px solid ${T.border}` }}>
                            {Object.values(row).map((v: any, j) => (
                              <td key={j} style={{ padding: '8px 12px', fontFamily: 'Menlo, monospace', color: T.ink, whiteSpace: 'nowrap', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {v === null ? <span style={{ color: T.inkLight }}>NULL</span>
                                  : typeof v === 'object' ? JSON.stringify(v)
                                  : String(v)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ padding: 24, color: T.inkLight, fontSize: 13 }}>No rows returned.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Users ── */}
        {tab === 'users' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 26, color: T.ink, margin: 0 }}>
                Users {users ? `· ${users.total} total` : ''}
              </h1>
              <button style={btn()} onClick={() => setShowAddUser(true)}>+ Add User</button>
            </div>
            {users && (
              <div style={{ background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}`, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: T.parchmentLight }}>
                    {['Name','Email','Role','Parish','Verified','Active','Joined',''].map(h => (
                      <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.inkLight }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {(users.users ?? []).map((u: any) => (
                      <tr key={u.id} style={{ borderBottom: `0.5px solid ${T.border}` }}>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: T.ink }}>{u.firstName} {u.lastName}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: T.ink }}>{u.email}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: T.ink }}>{u.role}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: T.ink }}>{churchMap[u.churchId] ?? `#${u.churchId}`}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: u.emailVerified ? T.success : T.error }}>{u.emailVerified ? '✓' : '✗'}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: u.isActive ? T.success : T.error }}>{u.isActive ? '✓' : '✗'}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: T.inkLight }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button style={btnSm(T.gold)} onClick={() => setEditingUser(u)}>Edit</button>
                            <button style={btnSm('#B91C1C')} onClick={() => deleteUser(u.id, `${u.firstName} ${u.lastName}`)}>Remove</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Finance ── */}
        {tab === 'finance' && (
          <div>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 26, color: T.ink, marginBottom: 24 }}>Financial Report</h1>
            {finance && (
              <>
                <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
                  <StatCard label="Gross Donations"  value={`R${parseFloat(finance.summary?.gross ?? 0).toFixed(2)}`}       color={T.success} />
                  <StatCard label="Platform Revenue"  value={`R${parseFloat(finance.summary?.platformFee ?? 0).toFixed(2)}`} color={T.gold} />
                  <StatCard label="Church Payouts"    value={`R${parseFloat(finance.summary?.churchShare ?? 0).toFixed(2)}`} />
                  <StatCard label="Avg Donation"      value={`R${parseFloat(finance.summary?.avg ?? 0).toFixed(2)}`}         sub={`${finance.summary?.count ?? 0} donations`} />
                </div>
                <div style={{ background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}`, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 20px', borderBottom: `0.5px solid ${T.border}`, fontFamily: 'Georgia, serif', fontSize: 16, fontWeight: 600, color: T.ink }}>By Church</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ background: T.parchmentLight }}>
                      {['Church','Gross','Church Share','Donations'].map(h => (
                        <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.inkLight }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {(finance.byChurch ?? []).map((r: any, i: number) => (
                        <tr key={i} style={{ borderBottom: `0.5px solid ${T.border}` }}>
                          {[r.church, `R${parseFloat(r.gross).toFixed(2)}`, `R${parseFloat(r.churchShare).toFixed(2)}`, r.count].map((c, j) => (
                            <td key={j} style={{ padding: '10px 14px', fontSize: 13, color: T.ink }}>{c}</td>
                          ))}
                        </tr>
                      ))}
                      {!(finance.byChurch?.length) && <tr><td colSpan={4} style={{ padding: '16px 14px', fontSize: 13, color: T.inkLight, textAlign: 'center' }}>No data yet</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Promo Codes ── */}
        {tab === 'promo' && (
          <div>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 26, color: T.ink, marginBottom: 24 }}>Promo Codes</h1>
            <div style={{ background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}`, padding: 20, marginBottom: 24 }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: 16, fontWeight: 600, color: T.ink, marginBottom: 14 }}>Generate Codes</div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, color: T.inkLight, marginBottom: 4 }}>COUNT</label>
                  <input type="number" value={promoCount} onChange={e => setPromoCount(parseInt(e.target.value))} min={1} max={100} style={{ width: 80, padding: '8px 12px', border: `0.5px solid ${T.border}`, borderRadius: 8, fontSize: 14 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 11, color: T.inkLight, marginBottom: 4 }}>DESCRIPTION</label>
                  <input type="text" value={promoDesc} onChange={e => setPromoDesc(e.target.value)} placeholder="e.g. Retirees batch April 2025" style={{ width: '100%', padding: '8px 12px', border: `0.5px solid ${T.border}`, borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                <button style={btn()} onClick={generatePromos}>Generate</button>
              </div>
            </div>
            {promo && (
              <div style={{ background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}`, overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', borderBottom: `0.5px solid ${T.border}`, display: 'flex', gap: 24 }}>
                  {[['Total', promo.total], ['Used', promo.used], ['Available', promo.active]].map(([l, v]) => (
                    <div key={l as string}><div style={{ fontFamily: 'Georgia, serif', fontSize: 22, fontWeight: 700, color: T.ink }}>{v}</div><div style={{ fontSize: 11, color: T.inkLight }}>{l as string}</div></div>
                  ))}
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: T.parchmentLight }}>
                    {['Code','Description','Status','Created'].map(h => (
                      <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.inkLight }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {(promo.recent ?? []).map((p: any) => (
                      <tr key={p.id} style={{ borderBottom: `0.5px solid ${T.border}` }}>
                        {[p.code, p.description ?? '—', p.isUsed ? '✓ Used' : 'Available', new Date(p.createdAt).toLocaleDateString()].map((c, i) => (
                          <td key={i} style={{ padding: '10px 14px', fontSize: 13, color: T.ink }}>{c}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Config ── */}
        {tab === 'config' && (
          <div>
            <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 26, color: T.ink, marginBottom: 24 }}>App Configuration</h1>
            <div style={{ background: T.white, borderRadius: 12, border: `0.5px solid ${T.border}`, padding: 20 }}>
              <p style={{ fontSize: 14, color: T.inkLight, marginBottom: 16 }}>These settings are served to the app on startup.</p>
              {[
                ['app_name','App display name'],['app_tagline','Tagline shown on login'],
                ['default_translation','Default Bible translation (CPDV or DRB)'],
                ['donation_min_zar','Minimum donation (ZAR)'],
                ['donation_suggestions','Suggested amounts, comma-separated'],
                ['support_email','User-facing support email'],
                ['maintenance_mode','Set to "true" to put app in read-only mode'],
              ].map(([key, desc]) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.inkLight, marginBottom: 4, letterSpacing: '0.5px', textTransform: 'uppercase' }}>{key}</label>
                  <div style={{ fontSize: 12, color: T.inkLight, marginBottom: 4 }}>{desc}</div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="text" id={key} placeholder="Current value from DB" style={{ flex: 1, padding: '8px 12px', border: `0.5px solid ${T.border}`, borderRadius: 8, fontSize: 13 }} />
                    <button style={btn()} onClick={() => { const el = document.getElementById(key) as HTMLInputElement; if (el?.value) api.patch(`/admin/config/${key}`, { value: el.value }); }}>Save</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Music Album Modals ── */}
      {showAddAlbum && (
        <MusicAlbumModal
          album={null}
          onClose={() => setShowAddAlbum(false)}
          onSaved={() => { setShowAddAlbum(false); loadMusic(); }}
        />
      )}
      {editingAlbum && (
        <MusicAlbumModal
          album={editingAlbum}
          onClose={() => setEditingAlbum(null)}
          onSaved={() => { setEditingAlbum(null); loadMusic(); }}
        />
      )}

      {/* ── Add User Modal ── */}
      {showAddUser && (
        <AddUserModal
          churches={churches}
          onClose={() => { setShowAddUser(false); }}
          onSaved={() => { setShowAddUser(false); loadUsers(); api.get('/admin/dashboard').then(r => setData(r.data.data)); }}
        />
      )}

      {/* ── Edit User Modal ── */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          churches={churches}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); refreshUsers(); }}
        />
      )}

      {/* ── Add Church Modal ── */}
      {showAddChurch && (
        <AddChurchModal
          onClose={() => { setShowAddChurch(false); }}
          onSaved={() => { setShowAddChurch(false); loadChurches(); api.get('/admin/dashboard').then(r => setData(r.data.data)); }}
        />
      )}

      {/* ── Edit Church Modal ── */}
      {editingChurch && (
        <EditChurchModal
          church={editingChurch}
          onClose={() => setEditingChurch(null)}
          onSaved={() => { setEditingChurch(null); loadChurches(); api.get('/admin/dashboard').then(r => setData(r.data.data)); }}
        />
      )}
    </div>
  );
}

// ── Music album editor: details, AI provenance, and tracks ──
// Provenance is not paperwork — the backend refuses to publish an album
// without an AI tool + licence reference, so the commercial-rights record
// always travels with the audio.
function MusicAlbumModal({ album, onClose, onSaved }: {
  album: any | null; onClose: () => void; onSaved: () => void;
}) {
  const api2 = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
  const isNew = !album;

  const [form, setForm] = useState({
    title:        album?.title ?? '',
    artist:       album?.artist ?? '',
    genre:        album?.genre ?? 'gospel',
    description:  album?.description ?? '',
    coverUrl:     album?.coverUrl ?? '',
    aiTool:       album?.aiTool ?? 'Suno',
    aiPrompt:     album?.aiPrompt ?? '',
    generatedAt:  album?.generatedAt ?? new Date().toISOString().split('T')[0],
    licenseRef:   album?.licenseRef ?? '',
    lyricsAuthor: album?.lyricsAuthor ?? 'ai',
  });
  const [tracks, setTracks] = useState<any[]>([]);
  const [tab, setTab] = useState<'details'|'provenance'|'tracks'>('details');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // New track draft
  const blankTrack = { title: '', streamUrl: '', durationSecs: '', lyricsText: '', lyricsTimestamps: '' };
  const [draft, setDraft] = useState(blankTrack);
  const [editingTrackId, setEditingTrackId] = useState<number|null>(null);
  const [checkingUrl, setCheckingUrl] = useState(false);

  const loadTracks = () => {
    if (!album?.id) return;
    api2.get(`/admin/music/albums/${album.id}`)
      .then(r => setTracks((r.data?.data ?? r.data)?.tracks ?? []))
      .catch(() => setTracks([]));
  };
  useEffect(() => {
    loadTracks();
  // loadTracks is recreated each render; re-fetch only when the album changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [album?.id]);

  const saveAlbum = async () => {
    if (!form.title.trim()) { setErr('Album title is required.'); setTab('details'); return; }
    setSaving(true); setErr('');
    try {
      if (isNew) await api2.post('/admin/music/albums', form);
      else       await api2.patch(`/admin/music/albums/${album.id}`, form);

      // A half-filled track in the Add-a-track form is easy to leave behind:
      // commit it here too rather than silently discarding the user's typing.
      if (!isNew && draft.title.trim() && draft.streamUrl.trim()) {
        const body = {
          ...draft,
          durationSecs: parseInt(draft.durationSecs || '0', 10) || 0,
          lyricsTimestamps: parseTimings(draft.lyricsTimestamps) ?? null,
        };
        if (editingTrackId) await api2.patch(`/admin/music/tracks/${editingTrackId}`, body);
        else                await api2.post(`/admin/music/albums/${album.id}/tracks`, body);
      }
      onSaved();
    } catch (e: any) {
      setErr(e.response?.data?.message ?? 'Save failed.');
    } finally { setSaving(false); }
  };

  // Turn the pasted .timings.json into the array the app expects. Returns
  // undefined when the box is empty, or throws with a readable message.
  const parseTimings = (raw: string) => {
    if (!raw.trim()) return undefined;
    let parsed: any;
    try { parsed = JSON.parse(raw); }
    catch { throw new Error('Lyric timings must be valid JSON (paste the whole .timings.json file).'); }
    if (!Array.isArray(parsed) || !parsed.every(l => typeof l?.startMs === 'number' && typeof l?.text === 'string')) {
      throw new Error('Lyric timings must be a list of { startMs, endMs, text } entries.');
    }
    return parsed;
  };

  // Load an existing track into the form to edit it, or clear back to "add".
  const editTrack = (t: any) => {
    setEditingTrackId(t.id);
    setDraft({
      title:        t.title ?? '',
      streamUrl:    t.streamUrl ?? '',
      durationSecs: t.durationSecs ? String(t.durationSecs) : '',
      lyricsText:   t.lyricsText ?? '',
      lyricsTimestamps: t.lyricsTimestamps ? JSON.stringify(t.lyricsTimestamps) : '',
    });
    setErr('');
  };
  const cancelEdit = () => { setEditingTrackId(null); setDraft(blankTrack); setErr(''); };

  const saveTrack = async () => {
    if (!draft.title.trim() || !draft.streamUrl.trim()) {
      setErr('A track needs a title and an audio URL.'); return;
    }
    setErr(''); setCheckingUrl(true);
    try {
      const body = {
        ...draft,
        durationSecs: parseInt(draft.durationSecs || '0', 10) || 0,
        lyricsTimestamps: parseTimings(draft.lyricsTimestamps) ?? null,
      };
      if (editingTrackId) await api2.patch(`/admin/music/tracks/${editingTrackId}`, body);
      else                await api2.post(`/admin/music/albums/${album.id}/tracks`, body);
      setEditingTrackId(null);
      setDraft(blankTrack);
      loadTracks();
    } catch (e: any) {
      setErr(e.response?.data?.message ?? e.message ?? 'Could not save track.');
    } finally { setCheckingUrl(false); }
  };

  const removeTrack = async (id: number, title: string) => {
    if (!window.confirm(`Remove track "${title}"?`)) return;
    await api2.delete(`/admin/music/tracks/${id}`);
    loadTracks();
  };

  const TABS: Array<['details'|'provenance'|'tracks', string]> = [
    ['details',    'Details'],
    ['provenance', 'AI & Licence'],
    ...(isNew ? [] : [['tracks', `Tracks${tracks.length ? ` (${tracks.length})` : ''}`] as ['tracks', string]]),
  ];

  return (
    <Modal title={isNew ? 'New Album' : `Edit ${album.title}`} onClose={onClose}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${T.border}` }}>
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '10px 14px',
              fontSize: 13, fontFamily: 'system-ui',
              color: tab === k ? T.burgundy : T.inkLight,
              borderBottom: tab === k ? `2px solid ${T.burgundy}` : '2px solid transparent',
              fontWeight: tab === k ? 600 : 400, marginBottom: -1,
            }}>{label}</button>
        ))}
      </div>

      {tab === 'details' && (
        <>
          <Field label="Album title"><input style={inp} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Songs of Praise" /></Field>
          <Field label="Artist"><input style={inp} value={form.artist} onChange={e => setForm({ ...form, artist: e.target.value })} placeholder="Echoes of the Word" /></Field>
          <Field label="Genre">
            <select style={inp} value={form.genre} onChange={e => setForm({ ...form, genre: e.target.value })}>
              {['gospel','afro_house','contemporary','hymns','chant','taize'].map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </Field>
          <Field label="Description"><input style={inp} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Original music for prayer and reflection" /></Field>
          <Field label="Cover image URL (optional)"><input style={inp} value={form.coverUrl} onChange={e => setForm({ ...form, coverUrl: e.target.value })} placeholder="https://…" /></Field>
        </>
      )}

      {tab === 'provenance' && (
        <>
          <div style={{ fontSize: 12, color: T.inkLight, marginBottom: 14, lineHeight: 1.6 }}>
            Required before publishing. This is your proof of commercial rights if the music is ever questioned.
          </div>
          <Field label="AI tool"><input style={inp} value={form.aiTool} onChange={e => setForm({ ...form, aiTool: e.target.value })} placeholder="Suno v4 (Pro plan)" /></Field>
          <Field label="Licence reference"><input style={inp} value={form.licenseRef} onChange={e => setForm({ ...form, licenseRef: e.target.value })} placeholder="Suno Pro invoice #12345 / subscription active 2026-08" /></Field>
          <Field label="Date generated"><input style={inp} type="date" value={form.generatedAt} onChange={e => setForm({ ...form, generatedAt: e.target.value })} /></Field>
          <Field label="Lyrics author"><input style={inp} value={form.lyricsAuthor} onChange={e => setForm({ ...form, lyricsAuthor: e.target.value })} placeholder="ai — or a person's name if human-written" /></Field>
          <Field label="Prompt used">
            <textarea style={{ ...inp, minHeight: 70, resize: 'vertical', fontFamily: 'inherit' }} value={form.aiPrompt}
              onChange={e => setForm({ ...form, aiPrompt: e.target.value })}
              placeholder="uplifting Afro house, 118bpm, gospel choir vocals, organ, original praise lyrics" />
          </Field>
        </>
      )}

      {tab === 'tracks' && !isNew && (
        <div>
          <div style={{ fontSize: 12, color: T.inkLight, marginBottom: 14, lineHeight: 1.6 }}>
            Upload the MP3 to your R2 bucket, then paste its public URL here.
          </div>
          {tracks.map((t: any) => (
            <div key={t.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
              border: `0.5px solid ${editingTrackId === t.id ? T.burgundy : T.border}`,
              background: editingTrackId === t.id ? `${T.burgundy}0D` : 'transparent',
              borderRadius: 8, marginBottom: 6,
            }}>
              <span style={{ fontSize: 12, color: T.inkLight, minWidth: 18 }}>{t.trackNumber}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: T.ink, fontWeight: 600 }}>{t.title}</div>
                <div style={{ fontSize: 11, color: T.inkLight }}>
                  {t.durationSecs ? `${Math.floor(t.durationSecs / 60)}:${String(t.durationSecs % 60).padStart(2, '0')}` : '—'}
                  {t.lyricsText ? ' · lyrics' : ''}
                  {t.lyricsTimestamps ? ' · synced' : ''}
                </div>
              </div>
              <button onClick={() => editTrack(t)} style={btnSm(T.gold)}>Edit</button>
              <button onClick={() => removeTrack(t.id, t.title)} style={{ background: 'none', border: 'none', color: T.error, cursor: 'pointer', padding: 6 }}>✕</button>
            </div>
          ))}

          <div style={{ border: `1px dashed ${T.border}`, borderRadius: 8, padding: 12, marginTop: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.ink }}>
                {editingTrackId ? 'Edit track' : 'Add a track'}
              </div>
              {editingTrackId && (
                <button onClick={cancelEdit} style={{ background: 'none', border: 'none', color: T.inkLight, fontSize: 12, cursor: 'pointer' }}>
                  Cancel edit
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 8 }}>
              <input style={inp} value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} placeholder="Track title" />
              <input style={inp} value={draft.durationSecs} onChange={e => setDraft({ ...draft, durationSecs: e.target.value })} placeholder="Duration (secs)" />
            </div>
            <input style={{ ...inp, marginBottom: 8 }} value={draft.streamUrl} onChange={e => setDraft({ ...draft, streamUrl: e.target.value })} placeholder="https://…r2.dev/music/track.mp3" />
            <textarea style={{ ...inp, minHeight: 70, resize: 'vertical', fontFamily: 'inherit', marginBottom: 8 }}
              value={draft.lyricsText} onChange={e => setDraft({ ...draft, lyricsText: e.target.value })}
              placeholder="Lyrics (optional — paste from Suno)" />
            <textarea style={{ ...inp, minHeight: 60, resize: 'vertical', fontFamily: 'monospace', fontSize: 11, marginBottom: 8 }}
              value={draft.lyricsTimestamps} onChange={e => setDraft({ ...draft, lyricsTimestamps: e.target.value })}
              placeholder='Synced lyric timings (optional) — paste the whole .timings.json, e.g. [{"startMs":27180,"endMs":31660,"text":"Morning breaks on my face"}]' />
            <button onClick={saveTrack} disabled={checkingUrl} style={{ ...btnSm(), opacity: checkingUrl ? 0.6 : 1 }}>
              {checkingUrl ? 'Checking audio URL…' : editingTrackId ? 'Save track' : '+ Add track'}
            </button>
          </div>
        </div>
      )}

      {err && <div style={{ color: T.error, fontSize: 13, marginTop: 12 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button onClick={onClose} style={{ background: T.parchmentLight, color: T.ink, border: `0.5px solid ${T.border}`, borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontSize: 13 }}>Close</button>
        <button onClick={saveAlbum} disabled={saving} style={btn()}>{saving ? 'Saving…' : isNew ? 'Create album' : 'Save changes'}</button>
      </div>
    </Modal>
  );
}

function EditChurchModal({ church, onClose, onSaved }: {
  church: any; onClose: () => void; onSaved: () => void;
}) {
  const api2 = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
  const [form, setForm] = useState({
    name:             church.name ?? '',
    address:          church.address ?? '',
    city:             church.city ?? '',
    country:          church.country ?? 'South Africa',
    email:            church.email ?? '',
    phone:            church.phone ?? '',
    website:          church.website ?? '',
    massTimes:        (church.massTimes ?? []) as Array<{ day: string; time: string; language: string }>,
    confessionTimes:  (church.confessionTimes ?? []) as Array<{ day: string; time: string; note: string }>,
    specialFeasts:    (church.specialFeasts ?? []) as Array<{ date: string; title: string; time: string; description: string }>,
  });
  const [tab, setTab] = useState<'info'|'mass'|'confession'|'events'>('info');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Load the full church (with massTimes/confessionTimes that the list endpoint
  // may have dropped from its SELECT).
  useEffect(() => {
    api2.get(`/churches/${church.id}`).then(({ data }) => {
      const ch = data?.data ?? data;
      if (!ch) return;
      setForm(f => ({
        ...f,
        massTimes:       ch.massTimes ?? f.massTimes,
        confessionTimes: ch.confessionTimes ?? f.confessionTimes,
        // Normalise legacy { date, description } feasts into events.
        specialFeasts:   ((ch.specialFeasts ?? f.specialFeasts) as any[]).map(e => ({
          date: e.date ?? '', title: e.title ?? e.description ?? '',
          time: e.time ?? '', description: e.description ?? '',
        })),
      }));
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [church.id]);

  const updateMass = (i: number, k: 'day'|'time'|'language', v: string) =>
    setForm(f => ({ ...f, massTimes: f.massTimes.map((m,idx) => idx===i ? { ...m, [k]: v } : m) }));
  const addMass    = () => setForm(f => ({ ...f, massTimes: [...f.massTimes, { day:'Sunday', time:'09:00', language:'English' }] }));
  const removeMass = (i: number) => setForm(f => ({ ...f, massTimes: f.massTimes.filter((_,idx) => idx!==i) }));

  const updateConf = (i: number, k: 'day'|'time'|'note', v: string) =>
    setForm(f => ({ ...f, confessionTimes: f.confessionTimes.map((c,idx) => idx===i ? { ...c, [k]: v } : c) }));
  const addConf    = () => setForm(f => ({ ...f, confessionTimes: [...f.confessionTimes, { day:'Saturday', time:'17:00', note:'Before Vigil Mass' }] }));
  const removeConf = (i: number) => setForm(f => ({ ...f, confessionTimes: f.confessionTimes.filter((_,idx) => idx!==i) }));

  const updateEvent = (i: number, k: 'date'|'title'|'time'|'description', v: string) =>
    setForm(f => ({ ...f, specialFeasts: f.specialFeasts.map((e,idx) => idx===i ? { ...e, [k]: v } : e) }));
  const addEvent    = () => setForm(f => ({ ...f, specialFeasts: [...f.specialFeasts, { date:'', title:'', time:'', description:'' }] }));
  const removeEvent = (i: number) => setForm(f => ({ ...f, specialFeasts: f.specialFeasts.filter((_,idx) => idx!==i) }));

  const save = async () => {
    // Drop blank event rows; every remaining event needs a date + title.
    const events = form.specialFeasts.filter(e => e.date.trim() || e.title.trim());
    if (events.some(e => !e.date.trim() || !e.title.trim())) {
      setErr('Each event needs both a date (YYYY-MM-DD) and a title.');
      setTab('events');
      return;
    }
    setSaving(true); setErr('');
    try {
      await api2.patch(`/admin/churches/${church.id}`, { ...form, specialFeasts: events });
      onSaved();
    } catch (e: any) {
      setErr(e.response?.data?.message ?? 'Save failed.');
    } finally { setSaving(false); }
  };

  const TABS: Array<['info'|'mass'|'confession'|'events', string]> = [
    ['info',       'Info'],
    ['mass',       `Mass Times${form.massTimes.length ? ` (${form.massTimes.length})` : ''}`],
    ['confession', `Confession${form.confessionTimes.length ? ` (${form.confessionTimes.length})` : ''}`],
    ['events',     `Events${form.specialFeasts.length ? ` (${form.specialFeasts.length})` : ''}`],
  ];

  return (
    <Modal title={`Edit ${church.name}`} onClose={onClose}>
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${T.border}` }}>
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '10px 14px', fontSize: 13, fontFamily: 'system-ui',
              color: tab === k ? T.burgundy : T.inkLight,
              borderBottom: tab === k ? `2px solid ${T.burgundy}` : '2px solid transparent',
              fontWeight: tab === k ? 600 : 400,
              marginBottom: -1,
            }}
          >{label}</button>
        ))}
      </div>

      {/* Info tab */}
      {tab === 'info' && (
        <>
          <Field label="Name"><input style={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Address"><input style={inp} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></Field>
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}><Field label="City"><input style={inp} value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></Field></div>
            <div style={{ flex: 1 }}><Field label="Country"><input style={inp} value={form.country} onChange={e => setForm({ ...form, country: e.target.value })} /></Field></div>
          </div>
          <Field label="Email"><input style={inp} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Phone"><input style={inp} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="Website"><input style={inp} value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} /></Field>
        </>
      )}

      {/* Mass Times tab */}
      {tab === 'mass' && (
        <div style={{ maxHeight: 360, overflow: 'auto' }}>
          {form.massTimes.length === 0 && (
            <div style={{ color: T.inkLight, fontSize: 13, textAlign: 'center', padding: 24 }}>
              No mass times set. Click "Add Mass Time" below.
            </div>
          )}
          {form.massTimes.map((m, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <select style={inp} value={m.day} onChange={e => updateMass(i, 'day', e.target.value)}>
                {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map(d => <option key={d}>{d}</option>)}
              </select>
              <input style={inp} value={m.time} onChange={e => updateMass(i, 'time', e.target.value)} placeholder="09:00" />
              <input style={inp} value={m.language} onChange={e => updateMass(i, 'language', e.target.value)} placeholder="English" />
              <button onClick={() => removeMass(i)} style={{ background: 'none', border: 'none', color: T.error, cursor: 'pointer', padding: 6 }}>✕</button>
            </div>
          ))}
          <button onClick={addMass} style={{ background: 'none', border: `1px dashed ${T.burgundy}`, color: T.burgundy, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontFamily: 'system-ui', cursor: 'pointer', width: '100%', marginTop: 8 }}>+ Add Mass Time</button>
        </div>
      )}

      {/* Confession Times tab */}
      {tab === 'confession' && (
        <div style={{ maxHeight: 360, overflow: 'auto' }}>
          {form.confessionTimes.length === 0 && (
            <div style={{ color: T.inkLight, fontSize: 13, textAlign: 'center', padding: 24 }}>
              No confession times set. Click "Add Confession Time" below.
            </div>
          )}
          {form.confessionTimes.map((c, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
              <select style={inp} value={c.day} onChange={e => updateConf(i, 'day', e.target.value)}>
                {['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'].map(d => <option key={d}>{d}</option>)}
              </select>
              <input style={inp} value={c.time} onChange={e => updateConf(i, 'time', e.target.value)} placeholder="17:00" />
              <input style={inp} value={c.note} onChange={e => updateConf(i, 'note', e.target.value)} placeholder="e.g. Before Vigil Mass" />
              <button onClick={() => removeConf(i)} style={{ background: 'none', border: 'none', color: T.error, cursor: 'pointer', padding: 6 }}>✕</button>
            </div>
          ))}
          <button onClick={addConf} style={{ background: 'none', border: `1px dashed ${T.burgundy}`, color: T.burgundy, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontFamily: 'system-ui', cursor: 'pointer', width: '100%', marginTop: 8 }}>+ Add Confession Time</button>
        </div>
      )}

      {/* Events tab */}
      {tab === 'events' && (
        <div style={{ maxHeight: 360, overflow: 'auto' }}>
          <div style={{ color: T.inkLight, fontSize: 12, marginBottom: 12 }}>
            Upcoming parish events shown to members. Date is required (YYYY-MM-DD).
          </div>
          {form.specialFeasts.length === 0 && (
            <div style={{ color: T.inkLight, fontSize: 13, textAlign: 'center', padding: 24 }}>
              No events set. Click "Add Event" below.
            </div>
          )}
          {form.specialFeasts.map((e, i) => (
            <div key={i} style={{ border: `0.5px solid ${T.border}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input style={inp} value={e.title} onChange={ev => updateEvent(i, 'title', ev.target.value)} placeholder="Event title (e.g. Parish Feast Day)" />
                <input style={inp} value={e.time} onChange={ev => updateEvent(i, 'time', ev.target.value)} placeholder="Time (optional)" />
                <button onClick={() => removeEvent(i)} style={{ background: 'none', border: 'none', color: T.error, cursor: 'pointer', padding: 6 }}>✕</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 8 }}>
                <input style={inp} value={e.date} onChange={ev => updateEvent(i, 'date', ev.target.value)} placeholder="2026-07-15" />
                <input style={inp} value={e.description} onChange={ev => updateEvent(i, 'description', ev.target.value)} placeholder="Details (optional)" />
              </div>
            </div>
          ))}
          <button onClick={addEvent} style={{ background: 'none', border: `1px dashed ${T.burgundy}`, color: T.burgundy, padding: '10px 16px', borderRadius: 8, fontSize: 13, fontFamily: 'system-ui', cursor: 'pointer', width: '100%', marginTop: 8 }}>+ Add Event</button>
        </div>
      )}

      {err && <div style={{ color: T.error, fontSize: 13, marginTop: 12 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button onClick={onClose} style={{ background: T.parchmentLight, color: T.ink, border: `0.5px solid ${T.border}`, borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
        <button onClick={save} disabled={saving} style={btn()}>{saving ? 'Saving…' : 'Save all changes'}</button>
      </div>
    </Modal>
  );
}

function EditUserModal({ user, churches, onClose, onSaved }: { user: any; churches: any[]; onClose: () => void; onSaved: () => void }) {
  const api2 = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
  const [form, setForm] = useState({
    firstName: user.firstName ?? '', lastName: user.lastName ?? '',
    email: user.email ?? '', role: user.role ?? 'member',
    churchId: String(user.churchId ?? ''), isActive: user.isActive ?? true,
    emailVerified: user.emailVerified ?? false,
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm(f => ({ ...f, [k]: e.target.value }));
  const setCheck = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.checked }));

  const save = async () => {
    setSaving(true); setErr('');
    try {
      await api2.patch(`/admin/users/${user.id}`, {
        ...form, churchId: Number(form.churchId),
      });
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? 'Failed to update user.');
    } finally { setSaving(false); }
  };

  return (
    <Modal title={`Edit User — ${user.firstName} ${user.lastName}`} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field label="First Name"><input style={inp} value={form.firstName} onChange={set('firstName')} /></Field>
        <Field label="Last Name"><input style={inp} value={form.lastName} onChange={set('lastName')} /></Field>
      </div>
      <Field label="Email"><input style={inp} type="email" value={form.email} onChange={set('email')} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field label="Parish">
          <select style={inp} value={form.churchId} onChange={set('churchId')}>
            {churches.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Role">
          <select style={inp} value={form.role} onChange={set('role')}>
            <option value="member">Member</option>
            <option value="church_admin">Church Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </Field>
      </div>
      <div style={{ display: 'flex', gap: 24, marginBottom: 14 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.ink, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.isActive} onChange={setCheck('isActive')} />
          Active account
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.ink, cursor: 'pointer' }}>
          <input type="checkbox" checked={form.emailVerified} onChange={setCheck('emailVerified')} />
          Email verified
        </label>
      </div>
      {err && <div style={{ color: T.error, fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
        <button style={btn(T.inkLight)} onClick={onClose}>Cancel</button>
        <button style={btn()} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
      </div>
    </Modal>
  );
}

function AddUserModal({ churches, onClose, onSaved }: { churches: any[]; onClose: () => void; onSaved: () => void }) {
  const api2 = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '', phone: '', churchId: churches[0]?.id ?? '', role: 'member' });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true); setErr('');
    try {
      await api2.post('/admin/users', { ...form, churchId: Number(form.churchId) });
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? 'Failed to create user.');
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Add User" onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field label="First Name"><input style={inp} value={form.firstName} onChange={set('firstName')} /></Field>
        <Field label="Last Name"><input style={inp} value={form.lastName} onChange={set('lastName')} /></Field>
      </div>
      <Field label="Email"><input style={inp} type="email" value={form.email} onChange={set('email')} /></Field>
      <Field label="Password"><input style={inp} type="password" value={form.password} onChange={set('password')} placeholder="Temporary password" /></Field>
      <Field label="Phone"><input style={inp} value={form.phone} onChange={set('phone')} placeholder="+27…" /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field label="Parish">
          <select style={inp} value={form.churchId} onChange={set('churchId')}>
            {churches.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Role">
          <select style={inp} value={form.role} onChange={set('role')}>
            <option value="member">Member</option>
            <option value="church_admin">Church Admin</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </Field>
      </div>
      {err && <div style={{ color: T.error, fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
        <button style={btn(T.inkLight)} onClick={onClose}>Cancel</button>
        <button style={btn()} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Create User'}</button>
      </div>
    </Modal>
  );
}

function AddChurchModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const api2 = axios.create({ baseURL: API, headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
  const [form, setForm] = useState({ name: '', address: '', city: '', country: 'South Africa', email: '', phone: '', website: '', donationSplitPct: '85' });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true); setErr('');
    try {
      await api2.post('/admin/churches', { ...form, donationSplitPct: Number(form.donationSplitPct) });
      onSaved();
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? 'Failed to create church.');
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Add Church" onClose={onClose}>
      <Field label="Church Name"><input style={inp} value={form.name} onChange={set('name')} placeholder="St Mary's Catholic Church" /></Field>
      <Field label="Address"><input style={inp} value={form.address} onChange={set('address')} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field label="City"><input style={inp} value={form.city} onChange={set('city')} /></Field>
        <Field label="Country"><input style={inp} value={form.country} onChange={set('country')} /></Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
        <Field label="Email"><input style={inp} type="email" value={form.email} onChange={set('email')} /></Field>
        <Field label="Phone"><input style={inp} value={form.phone} onChange={set('phone')} /></Field>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0 16px' }}>
        <Field label="Website"><input style={inp} value={form.website} onChange={set('website')} placeholder="https://…" /></Field>
        <Field label="Donation Split %"><input style={inp} type="number" min={0} max={100} value={form.donationSplitPct} onChange={set('donationSplitPct')} /></Field>
      </div>
      {err && <div style={{ color: T.error, fontSize: 13, marginBottom: 12 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
        <button style={btn(T.inkLight)} onClick={onClose}>Cancel</button>
        <button style={btn()} onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Create Church'}</button>
      </div>
    </Modal>
  );
}
